import React, { useState, useEffect } from 'react';
import { scheduleService } from '../../services/scheduleService';
import { apiService } from '../../services/api';
import TimezoneSelector from './TimezoneSelector';
import HostVoicePicker from './HostVoicePicker';
import { usePlan } from '../../hooks/usePlan';
import { openPricingModal } from '../../utils/pricingModal';
import { getPlanLimits } from '../../constants/planLimits';
import {
  POLLY_STANDARD_LANGUAGE_OPTIONS,
  getDefaultPollyVoiceForLanguage,
  getPollyLanguageName
} from '../../constants/pollyStandardVoices';

const CONVERSATIONAL_LANGUAGE_MODES = [
  { value: 'secondary-only', label: 'Second language only', description: 'Hosts speak only in your learning language' },
  { value: 'native-then-secondary', label: 'English + second language', description: 'Each line in English, then your learning language' },
  { value: 'native-only', label: 'English only', description: 'Hosts speak only in English' }
];

const CONVERSATIONAL_DIFFICULTIES = [
  { value: 'easy', label: 'Easy', description: 'Beginner (A2) — simple everyday words, short sentences' },
  { value: 'medium', label: 'Medium', description: 'Intermediate (B1–B2) — the standard style' },
  { value: 'hard', label: 'Hard', description: 'Advanced (C1) — natural native-level vocabulary' }
];

const needsTranslationLanguage = (mode) => mode === 'secondary-only' || mode === 'native-then-secondary';

function ScheduleEditor({ scheduleId, onComplete, onCancel }) {
  const { plan, resolved } = usePlan();
  const isFreePlan = plan === 'free';
  const planLimits = getPlanLimits(plan);
  // Articles-per-episode lock (Topic 5): only restrict once the plan has resolved from the
  // server (usePlan defaults to 'plus' while loading/on error, so isFreePlan is false during
  // that window regardless) — backend validation remains the real enforcement.
  const articlesLocked = resolved && isFreePlan;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [userSources, setUserSources] = useState([]);
  const [originalSchedule, setOriginalSchedule] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    scheduleName: '',
    timezone: '',
    refreshTimes: [],
    newsSources: [],
    audioSettings: {
      audioSpeed: '1.0',
      includeIntro: true,
      includeOutro: true
    },
    episodeFormat: 'article',
    conversationalLanguageMode: null,
    conversationalDifficulty: 'medium',
    hostVoices: null,
    articlesPerEpisode: 3
  });

  const loadScheduleData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load schedule
      const schedule = await scheduleService.getSchedule(scheduleId);
      setOriginalSchedule(schedule);

      // Populate form
      setFormData({
        scheduleName: schedule.scheduleName || '',
        timezone: schedule.timezone || '',
        refreshTimes: schedule.refreshTimes || [],
        newsSources: schedule.newsSources || [],
        // Merge over defaults (rather than an all-or-nothing fallback) so
        // older schedules saved before includeOutro existed still default
        // both intro and outro on, matching new-schedule defaults.
        audioSettings: {
          audioSpeed: '1.0',
          includeIntro: true,
          includeOutro: true,
          ...(schedule.audioSettings || {})
        },
        episodeFormat: schedule.episodeFormat || 'article',
        conversationalLanguageMode: schedule.conversationalLanguageMode || null,
        conversationalDifficulty: schedule.episodeFormat === 'conversational' ? (schedule.conversationalDifficulty || 'medium') : null,
        hostVoices: schedule.hostVoices || null,
        // Missing/null ⇒ default (3) — schedules saved before this field existed never had it
        // backfilled server-side, per the Topic 5 plan.
        articlesPerEpisode: schedule.articlesPerEpisode || 3
      });

      // Load user sources for selection
      // Note: We'll need to get userId from the schedule or auth
      const sources = await apiService.getUserSources(schedule.userId);
      setUserSources(sources || []);
    } catch (err) {
      console.error('Failed to load schedule:', err);
      setError(err.message || 'Failed to load schedule');
    } finally {
      setIsLoading(false);
    }
  };

  // Load schedule data
  useEffect(() => {
    loadScheduleData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAudioSettings = (field, value) => {
    setFormData(prev => ({
      ...prev,
      audioSettings: { ...prev.audioSettings, [field]: value }
    }));
  };

  const updateEpisodeFormat = (format) => {
    setFormData(prev => ({
      ...prev,
      episodeFormat: format,
      // Switching to article clears the mode; switching to conversational starts unselected
      // so the user must make an explicit choice (mirrors the backend requirement).
      conversationalLanguageMode: format === 'conversational' ? prev.conversationalLanguageMode : null,
      conversationalDifficulty: format === 'conversational' ? (prev.conversationalDifficulty || 'medium') : null,
      hostVoices: format === 'conversational' ? prev.hostVoices : null
    }));
  };

  const updateConversationalMode = (mode) => {
    updateFormData('conversationalLanguageMode', mode);
  };

  const setSourceTranslationLanguage = (languageCode) => {
    const translationVoiceId = languageCode ? getDefaultPollyVoiceForLanguage(languageCode) : null;
    setFormData(prev => ({
      ...prev,
      newsSources: prev.newsSources.map((source, idx) =>
        idx === 0 ? { ...source, translationLanguage: languageCode || null, translationVoiceId } : source
      )
    }));
  };

  const updateRefreshTime = (index, time) => {
    const newTimes = [...formData.refreshTimes];
    newTimes[index] = { ...newTimes[index], localTime: time };
    updateFormData('refreshTimes', newTimes);
  };

  const toggleRefreshTime = (index) => {
    const newTimes = [...formData.refreshTimes];
    newTimes[index] = { ...newTimes[index], enabled: !newTimes[index].enabled };
    updateFormData('refreshTimes', newTimes);
  };

  const addRefreshTime = () => {
    if (formData.refreshTimes.length < 2) {
      updateFormData('refreshTimes', [
        ...formData.refreshTimes,
        { localTime: '18:00', enabled: true }
      ]);
    }
  };

  const removeRefreshTime = (index) => {
    if (formData.refreshTimes.length > 1) {
      updateFormData('refreshTimes', formData.refreshTimes.filter((_, i) => i !== index));
    }
  };

  const toggleNewsSource = (source) => {
    const exists = formData.newsSources.find(s => s.url === source.url);
    if (exists) {
      updateFormData('newsSources', formData.newsSources.filter(s => s.url !== source.url));
    } else {
      // Limit to 1 news source per schedule
      updateFormData('newsSources', [{
        url: source.url,
        name: source.name,
        sourceLanguage: source.language || 'en',
        extractionType: source.extractionType || 'multiple'
      }]);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // Validate
      if (!formData.scheduleName.trim()) {
        throw new Error('Please enter a schedule name');
      }
      if (formData.newsSources.length === 0) {
        throw new Error('Please select at least one news source');
      }
      const enabledTimes = formData.refreshTimes.filter(t => t.enabled);
      if (enabledTimes.length === 0) {
        throw new Error('Please enable at least one refresh time');
      }
      if (formData.episodeFormat === 'conversational') {
        if (!formData.conversationalLanguageMode) {
          throw new Error('Please select a language mode for the podcast conversation format');
        }
        if (needsTranslationLanguage(formData.conversationalLanguageMode) &&
            !(formData.newsSources[0] && formData.newsSources[0].translationLanguage)) {
          throw new Error(`Language mode "${formData.conversationalLanguageMode}" requires a translation language for the selected news source`);
        }
      }

      // Free plan can't remove intro/outro — force both on regardless of
      // stored state (belt-and-braces; the checkboxes are disabled+checked
      // already, but server-side enforcement doesn't land until M1).
      const audioSettings = isFreePlan
        ? { ...formData.audioSettings, includeIntro: true, includeOutro: true }
        : formData.audioSettings;

      // Prepare update data
      const updateData = {
        scheduleName: formData.scheduleName,
        timezone: formData.timezone,
        refreshTimes: formData.refreshTimes,
        newsSources: formData.newsSources,
        audioSettings,
        episodeFormat: formData.episodeFormat,
        conversationalLanguageMode: formData.conversationalLanguageMode,
        conversationalDifficulty: formData.episodeFormat === 'conversational' ? (formData.conversationalDifficulty || 'medium') : null,
        hostVoices: formData.episodeFormat === 'conversational' ? formData.hostVoices : null,
        // Free plan can't save a higher count by editing the request body — force it here too
        // (belt-and-braces; the backend rejects it regardless).
        articlesPerEpisode: isFreePlan ? 3 : formData.articlesPerEpisode
      };

      console.log('Updating schedule with:', updateData);

      // Update schedule
      const updatedSchedule = await scheduleService.updateSchedule(scheduleId, updateData);
      
      console.log('Schedule updated successfully:', updatedSchedule);
      onComplete(updatedSchedule);
    } catch (err) {
      console.error('Failed to update schedule:', err);
      setError(err.message || 'Failed to update schedule');
      if (err.waitlisted) {
        // Structured 403 (§9, free-tier-cap-enforcement plan): open the pricing modal with
        // waitlist copy rather than leaving this as a plain inline error.
        openPricingModal();
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Episode style validity (mirrors backend validateScheduleConfig rules)
  const selectedSource = formData.newsSources[0];
  const requiresTranslationLanguage = formData.episodeFormat === 'conversational' &&
    needsTranslationLanguage(formData.conversationalLanguageMode);
  const hasTranslationLanguage = !!(selectedSource && selectedSource.translationLanguage);
  const episodeStyleValid = formData.episodeFormat === 'article' ||
    (!!formData.conversationalLanguageMode && (!requiresTranslationLanguage || hasTranslationLanguage));

  if (isLoading) {
    return (
      <div
        onClick={onCancel}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
      >
        <div style={{ background: 'white', padding: '3rem', borderRadius: '12px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <div>Loading schedule...</div>
        </div>
      </div>
    );
  }

  return (
    // Modal overlay
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
        overflowY: 'auto'
      }}
    >
      {/* Modal content */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '700px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, var(--color-sky) 0%, var(--color-primary) 100%)',
            color: 'white',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem' }}>✏️ Edit Schedule</h2>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
            {originalSchedule?.scheduleName}
          </p>
        </div>

        {/* Form content */}
        <div style={{ padding: '1.5rem' }}>
          {error && (
            <div
              style={{
                padding: '1rem',
                background: '#fed7d7',
                border: '1px solid #fc8181',
                borderRadius: '8px',
                color: '#742a2a',
                marginBottom: '1.5rem'
              }}
            >
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Schedule Name */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
              Schedule Name *
            </label>
            <input
              type="text"
              value={formData.scheduleName}
              onChange={(e) => updateFormData('scheduleName', e.target.value)}
              placeholder="e.g., Morning News Briefing"
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid #cbd5e0',
                fontSize: '1rem'
              }}
            />
          </div>

          {/* Timezone */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
              Timezone *
            </label>
            <TimezoneSelector
              value={formData.timezone}
              onChange={(tz) => updateFormData('timezone', tz)}
            />
          </div>

          {/* Refresh Times */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
              Refresh Times * (1-2 per day)
            </label>
            {formData.refreshTimes.map((time, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={time.enabled}
                  onChange={() => toggleRefreshTime(idx)}
                  style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                />
                <input
                  type="time"
                  value={time.localTime}
                  onChange={(e) => updateRefreshTime(idx, e.target.value)}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e0',
                    fontSize: '1rem'
                  }}
                />
                {formData.refreshTimes.length > 1 && (
                  <button
                    onClick={() => removeRefreshTime(idx)}
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '6px',
                      border: '1px solid #fc8181',
                      background: 'white',
                      color: '#c53030',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            {formData.refreshTimes.length < 2 && (
              <button
                onClick={addRefreshTime}
                className="btn btn-secondary btn-sm"
                style={{ marginTop: '0.5rem' }}
              >
                + Add Another Time
              </button>
            )}
          </div>

          {/* News Sources */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
              News Source * (select 1)
            </label>
            <div
              style={{
                border: '1px solid #cbd5e0',
                borderRadius: '8px',
                padding: '1rem',
                maxHeight: '300px',
                overflowY: 'auto'
              }}
            >
              {userSources.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#718096', padding: '1rem' }}>
                  No saved sources. Add sources in the main app first.
                </div>
              ) : (
                userSources.map((source, idx) => {
                  const isSelected = formData.newsSources.find(s => s.url === source.url);
                  return (
                    <div
                      key={idx}
                      onClick={() => toggleNewsSource(source)}
                      style={{
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        border: '1px solid ' + (isSelected ? 'var(--color-primary)' : '#e2e8f0'),
                        borderRadius: '6px',
                        background: isSelected ? '#edf2f7' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={!!isSelected}
                          readOnly
                          style={{ width: '18px', height: '18px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#2d3748' }}>{source.name}</div>
                          <div style={{ fontSize: '0.85rem', color: '#718096' }}>{source.url}</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Episode Style (Phase 1, conversational-podcast plan) */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
              Episode Style
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="episodeFormat"
                  checked={formData.episodeFormat === 'article'}
                  onChange={() => updateEpisodeFormat('article')}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>📖 Read the news</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="episodeFormat"
                  checked={formData.episodeFormat === 'conversational'}
                  onChange={() => updateEpisodeFormat('conversational')}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>🗣️ Podcast conversation</span>
              </label>
            </div>

            {formData.episodeFormat === 'conversational' && (
              <div style={{ marginTop: '1rem', paddingLeft: '1.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
                  Language Mode
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {CONVERSATIONAL_LANGUAGE_MODES.map(opt => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="radio"
                        name="conversationalLanguageMode"
                        checked={formData.conversationalLanguageMode === opt.value}
                        onChange={() => updateConversationalMode(opt.value)}
                        style={{ width: '18px', height: '18px', marginTop: '0.15rem' }}
                      />
                      <span>
                        <div>{opt.label}</div>
                        <div style={{ fontSize: '0.8rem', color: '#718096' }}>{opt.description}</div>
                      </span>
                    </label>
                  ))}
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
                    Difficulty
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {CONVERSATIONAL_DIFFICULTIES.map(opt => (
                      <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="conversationalDifficulty"
                          checked={(formData.conversationalDifficulty || 'medium') === opt.value}
                          onChange={() => updateFormData('conversationalDifficulty', opt.value)}
                          style={{ width: '18px', height: '18px', marginTop: '0.15rem' }}
                        />
                        <span>
                          <div>{opt.label}</div>
                          <div style={{ fontSize: '0.8rem', color: '#718096' }}>{opt.description}</div>
                        </span>
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '0.25rem' }}>
                    Difficulty shapes the English dialogue; translated lines follow from it.
                  </div>
                </div>

                {formData.conversationalLanguageMode && (
                  <HostVoicePicker
                    value={formData.hostVoices}
                    onChange={(hv) => setFormData(prev => ({ ...prev, hostVoices: hv }))}
                    secondaryLanguage={selectedSource?.translationLanguage || null}
                    needsSecondary={needsTranslationLanguage(formData.conversationalLanguageMode)}
                    languageMode={formData.conversationalLanguageMode}
                  />
                )}

                {requiresTranslationLanguage && hasTranslationLanguage && (
                  <div style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#718096' }}>
                    Translation language: <strong>{getPollyLanguageName(selectedSource.translationLanguage)}</strong>
                  </div>
                )}

                {requiresTranslationLanguage && !hasTranslationLanguage && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#718096' }}>
                      This mode needs a translation language for your selected news source:
                    </label>
                    <select
                      value=""
                      onChange={(e) => setSourceTranslationLanguage(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '6px',
                        border: '1px solid #cbd5e0',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="">Choose a language...</option>
                      {POLLY_STANDARD_LANGUAGE_OPTIONS.map(opt => (
                        <option key={opt.languageCode} value={opt.languageCode}>{opt.languageName}</option>
                      ))}
                    </select>
                    <div style={{
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      background: '#fff3cd',
                      border: '1px solid #F9C500',
                      borderRadius: '6px',
                      color: '#92400e',
                      fontSize: '0.85rem'
                    }}>
                      ⚠️ Select a translation language above before you can save.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Articles per episode (Topic 5, monetization plan) — plan-aware, server-enforced;
              this control is UI-convenience only, the backend rejects out-of-range values. */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: '500', color: '#4a5568' }}>
              Articles per episode
              {articlesLocked && <span className="plan-lock-hint">🔒 Plus</span>}
            </label>
            <select
              value={articlesLocked ? 3 : formData.articlesPerEpisode}
              disabled={articlesLocked}
              onChange={(e) => updateFormData('articlesPerEpisode', parseInt(e.target.value, 10))}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '6px',
                border: '1px solid #cbd5e0',
                fontSize: '1rem',
                background: articlesLocked ? '#f7fafc' : 'white',
                cursor: articlesLocked ? 'not-allowed' : 'pointer',
                color: articlesLocked ? '#718096' : 'inherit'
              }}
            >
              {Array.from({ length: planLimits.maxArticlesPerSource }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n} article{n === 1 ? '' : 's'}</option>
              ))}
            </select>
            <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.5rem' }}>
              {articlesLocked ? (
                <>
                  Free plan uses 3 articles per episode.{' '}
                  <button type="button" className="plan-upgrade-link" onClick={openPricingModal}>
                    Upgrade to Plus
                  </button>{' '}
                  to choose your own count.
                </>
              ) : (
                'Fewer articles make shorter, more focused episodes. More articles give broader coverage and may create longer episodes.'
              )}
            </div>
          </div>

          {/* Audio Settings */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '500', color: '#4a5568' }}>
              Audio Settings
            </label>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isFreePlan ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isFreePlan ? true : formData.audioSettings.includeIntro}
                  disabled={isFreePlan}
                  onChange={(e) => updateAudioSettings('includeIntro', e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Include Intro</span>
                {isFreePlan && <span className="plan-lock-hint">🔒 Plus</span>}
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: isFreePlan ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isFreePlan ? true : formData.audioSettings.includeOutro}
                  disabled={isFreePlan}
                  onChange={(e) => updateAudioSettings('includeOutro', e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Include Outro</span>
                {isFreePlan && <span className="plan-lock-hint">🔒 Plus</span>}
              </label>
            </div>
            {isFreePlan && (
              <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.75rem' }}>
                Intro and outro are included for all episodes on the free plan.{' '}
                <button type="button" className="plan-upgrade-link" onClick={openPricingModal}>
                  Upgrade to Plus
                </button>{' '}
                to remove them.
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: '#718096' }}>
                Audio Speed: {formData.audioSettings.audioSpeed}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={formData.audioSettings.audioSpeed}
                onChange={(e) => updateAudioSettings('audioSpeed', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid #e2e8f0',
            background: '#f7fafc',
            borderBottomLeftRadius: '12px',
            borderBottomRightRadius: '12px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem'
          }}
        >
          <button
            onClick={onCancel}
            disabled={isSaving}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !episodeStyleValid}
            className="btn btn-primary"
          >
            {isSaving ? '💾 Saving...' : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScheduleEditor;
